import type { Messages } from './messages';

export const es: Messages = {
  'lang.name': 'Español',
  'lang.aria': 'Idioma',
  'app.title': 'Sintetizador web',

  // Header — mapping shift
  'header.shift.aria': 'Desplazar el mapeo del teclado',
  'shift.octDown': '− 8ve',
  'shift.noteDown': '− nota',
  'shift.noteUp': '+ nota',
  'shift.octUp': '+ 8ve',
  'shift.octDown.title': 'Una octava más abajo (tecla −)',
  'shift.noteDown.title': 'Una nota blanca más abajo (tecla _)',
  'shift.noteUp.title': 'Una nota blanca más arriba (tecla +)',
  'shift.octUp.title': 'Una octava más arriba (tecla =)',

  // Install button
  'install.prompt': 'Instalar la aplicación',
  'install.installed': 'Instalada',
  'install.promptTitle': 'Instalar el sintetizador como aplicación',
  'install.uninstallHint':
    'La aplicación está instalada. Los navegadores no permiten desinstalar desde una página; usa el menú del navegador (⋮) → «Desinstalar», o las aplicaciones del sistema.',
  'install.manualHint':
    'Si no aparece automáticamente: en el menú del navegador (⋮) elige «Instalar aplicación». En iPhone/iPad: Compartir → «Añadir a pantalla de inicio».',

  // Metronome
  'metronome.label': 'Metrónomo',
  'metronome.tempoDown.aria': 'Tempo más lento',
  'metronome.tempoUp.aria': 'Tempo más rápido',
  'metronome.bpm.aria': 'Tempo (BPM)',
  'metronome.tap': 'Tap',
  'metronome.tap.aria': 'Marca el tempo haciendo clic',
  'metronome.start': 'Iniciar',
  'metronome.stop': 'Detener',
  'meter.aria': 'Compás (signatura de compás)',
  'meter.minus.aria': 'Menos pulsos',
  'meter.plus.aria': 'Más pulsos',
  'meter.accents.aria': 'Acentos en los pulsos',
  'meter.custom': 'Personalizado…',

  'sustain': 'Sustain',
  'reset.all': 'Restablecer todos los ajustes',

  // Section titles
  'section.keyboard': 'Teclado',
  'section.engine': 'Motor de sonido',
  'section.sound': 'Instrumento y sonido',
  'section.effects': 'Efectos',
  'section.curve': 'Curva de nivel por registro (graves ↔ agudos)',
  'section.offline': 'Disponibilidad sin conexión',

  // Keyboard section
  'labels.aria': 'Etiquetas en las teclas',
  'label.cde': 'CDE',
  'label.cde.title': 'Letras en las teclas (C D E …)',
  'label.doremi': 'DoReMi',
  'label.doremi.title': 'Solfeo en las teclas (Do Re Mi …)',
  'label.keys': 'Teclas',
  'label.keys.title': 'La tecla del teclado en cada nota',
  'label.octave': 'Octava',
  'label.octave.title': 'El número de octava, arriba en la tecla',
  'field.startNote': 'Nota de inicio',
  'field.startOctave': 'Octava de inicio',
  'field.octaveCount': 'Número de octavas',
  'field.displayMode': 'Modo de visualización',
  'display.scroll': 'Desplazamiento horizontal',
  'display.fit1': 'Escalado en una fila',
  'display.fit2': 'Escalado en dos filas',
  'range.label': 'Rango del teclado',
  'range.keys': '{n} teclas',
  'range.low.aria': 'Nota más grave',
  'range.high.aria': 'Nota más aguda',
  'range.drag.aria': 'Mover todo el rango',
  'octaveCount.full': 'Rango completo (88 teclas)',
  'octaveCount.partial': '— (rango parcial)',

  // Engine section
  'field.engine': 'Motor',
  'engine.synth': 'Sintetizado (Tone.js)',
  'engine.sampler': 'Sample real (instrumentos grabados)',
  'field.synthType': 'Tipo de sintetizador',
  'field.preset': 'Preset',
  'preset.aria': 'Preset de instrumento',
  'field.waveform': 'Timbre',
  'waveform.aria': 'Forma de onda (timbre)',
  'field.instrument': 'Instrumento',
  'field.density': 'Densidad de samples',
  'density.6': 'Rápido (1 sample / 6 semitonos)',
  'density.3': 'Equilibrado (1 sample / 3 semitonos)',
  'density.1': 'Completo (todos los samples)',

  // Sampler status
  'status.loading': 'Cargando «{instrument}»: {loaded} / {total} samples…',
  'status.upgrading': 'Reproducible ahora ({count} samples) · cargando la calidad completa…',
  'status.ready': 'Listo: {count} samples ({low}–{high}){failed}. Las notas intermedias se transponen.',
  'status.readyFailed': ' · {failed} fallidos',
  'status.cached': 'Instrumento cargado (desde la caché).',
  'status.errNoSamples': 'Este instrumento no tiene samples en la biblioteca.',
  'status.errLoadFailed': 'No se pudo cargar ningún sample (comprueba tu conexión).',

  // Keyboard loading overlay
  'overlay.loading': 'Cargando «{instrument}»… {loaded} / {total}',
  'overlay.preparing': 'Preparando el instrumento…',
  'overlay.error': 'No se pudieron cargar los samples: {message}',

  // Sound knobs
  'knob.attack': 'Ataque (s)',
  'knob.release': 'Caída (s)',
  'knob.volume': 'Volumen',
  'knob.filter': 'Brillo',
  'knob.filter.aria': 'Brillo (filtro paso bajo)',
  'knob.echo': 'Echo',
  'knob.reverb': 'Reverb',
  'resetInstrument': 'Restablecer a valores predeterminados',
  'resetInstrument.title': 'Devuelve el sonido de este instrumento a sus valores predeterminados',

  // Filter tone words
  'filter.veryDark': 'muy oscuro',
  'filter.dark': 'oscuro',
  'filter.neutral': 'neutro',
  'filter.bright': 'brillante',
  'filter.veryBright': 'muy brillante',

  // Register curve
  'curve.hint':
    'Compensación de sonoridad percibida para los osciladores sintetizados, que son técnicamente planos pero suenan pobres en los graves. Los instrumentos grabados aportan su propio equilibrio (medido plano dentro de ~2,7 dB de C1 a C7), así que por defecto la curva no se les aplica.',
  'curve.bassPre': 'Graves:',
  'curve.bassPost': 'dB / octava bajo Do4',
  'curve.treblePre': 'Agudos:',
  'curve.treblePost': 'dB / octava sobre Do4',
  'curve.onSamples': 'Aplicar también a samples reales',
  'curve.notApplied': 'La curva no se aplica al motor de samples (marca la casilla para aplicarla).',

  // Offline section
  'offline.hint':
    'Instala la aplicación desde el menú del navegador («Instalar» / «Añadir a pantalla de inicio»), luego descarga todos los samples reales en la caché. Después el sintetizador funciona completamente sin conexión, en cualquier dispositivo. La descarga es del orden de ~100–250 MB y se hace una sola vez.',
  'offline.download': 'Descargar todo para sin conexión',
  'offline.refresh': 'Actualizar la caché sin conexión',
  'offline.done': 'Todos los samples se han descargado — disponibles sin conexión.',
  'offline.downloading': 'Descargando {done} / {total} samples…{failed}',
  'offline.failedSuffix': ' ({failed} fallidos)',
  'offline.doneAll': 'Listo — los {total} samples están disponibles sin conexión.',
  'offline.doneWithErrors': 'Descargado con {failed} errores — reintenta para completar la caché.',

  // Engine notices
  'engine.fallback': 'No se pudo crear «{label}» ({reason}). Se volvió a Synth.',
  'worklet.hint':
    'La página se abre directamente desde el disco (file://), donde el navegador rechaza los módulos AudioWorklet: PluckSynth y BitCrusher no pueden funcionar. Ejecuta «npm run dev» y abre la dirección http que se muestra allí para que funcionen.',

  // Keyboard area
  'keyboard.aria': 'Teclado',
  'keyboard.resize.aria': 'Ajusta la altura del teclado (arrastra, o flechas arriba/abajo; doble clic = automático)',
  'keyboard.resize.title': 'Arrastra para la altura · doble clic = automático',

  // Presets
  'preset.piano': 'Piano',
  'preset.organ': 'Órgano',
  'preset.strings': 'Cuerdas',
  'preset.bass': 'Bajo',
  'preset.lead': 'Lead',
  'preset.custom': 'Personalizado',

  // Synth types
  'synth.synth': 'Synth',
  'synth.monosynth': 'MonoSynth',
  'synth.amsynth': 'AMSynth',
  'synth.fmsynth': 'FMSynth',
  'synth.duosynth': 'DuoSynth',
  'synth.membrane': 'MembraneSynth',
  'synth.metal': 'MetalSynth',
  'synth.noise': 'NoiseSynth (monofónico)',
  'synth.pluck': 'PluckSynth (monofónico)',

  // Waveforms
  'waveform.sine': 'Senoidal',
  'waveform.triangle': 'Triangular',
  'waveform.sawtooth': 'Diente de sierra',
  'waveform.square': 'Cuadrada',

  // Effects
  'effect.mix': 'Mix',
  'effect.chorus': 'Chorus',
  'effect.distortion': 'Distorsión',
  'effect.bitcrusher': 'BitCrusher',
  'effect.vibrato': 'Vibrato',
  'effect.autowah': 'AutoWah',
  'effect.phaser': 'Phaser',
  'effect.pingpong': 'Ping-pong delay',
  'effect.stereowidener': 'Stereo widener',
  'effect.eq3': 'EQ de 3 bandas',
  'effect.pitchshift': 'Pitch shift',

  // Parameters (shared by effects and synths, keyed by param key)
  'param.frequency': 'Frecuencia (Hz)',
  'param.distortion': 'Cantidad',
  'param.bits': 'Bits',
  'param.depth': 'Profundidad',
  'param.octaves': 'Octavas',
  'param.delayTime': 'Tiempo (s)',
  'param.width': 'Ancho',
  'param.low': 'Graves (dB)',
  'param.mid': 'Medios (dB)',
  'param.high': 'Agudos (dB)',
  'param.pitch': 'Semitonos',
  'param.harmonicity': 'Armonicidad',
  'param.modulationIndex': 'Índice de modulación',
  'param.vibratoAmount': 'Vibrato',
  'param.pitchDecay': 'Caída de tono (s)',
  'param.dampening': 'Amortiguación (Hz)',
  'param.resonance': 'Resonancia',
  'param.type': 'Color de ruido',

  // Instruments
  'instrument.piano': 'Piano',
  'instrument.guitar-acoustic': 'Guitarra acústica',
  'instrument.guitar-nylon': 'Guitarra de cuerdas de nylon',
  'instrument.guitar-electric': 'Guitarra eléctrica',
  'instrument.bass-electric': 'Bajo eléctrico',
  'instrument.bassoon': 'Fagot',
  'instrument.cello': 'Violonchelo',
  'instrument.clarinet': 'Clarinete',
  'instrument.contrabass': 'Contrabajo',
  'instrument.flute': 'Flauta',
  'instrument.french-horn': 'Trompa',
  'instrument.harmonium': 'Armonio',
  'instrument.harp': 'Arpa',
  'instrument.organ': 'Órgano',
  'instrument.saxophone': 'Saxofón',
  'instrument.trombone': 'Trombón',
  'instrument.trumpet': 'Trompeta',
  'instrument.tuba': 'Tuba',
  'instrument.violin': 'Violín',
  'instrument.xylophone': 'Xilófono',

  // Meters
  'meter.4/4': '4/4 (común)',
  'meter.3/4': '3/4 (vals)',
  'meter.2/4': '2/4',
  'meter.6/8': '6/8 (3+3)',
  'meter.5/4': '5/4 (Take Five · 3+2)',
  'meter.7/8': '7/8 (2+2+3)',
  'meter.9/8': '9/8 (balcánico · 2+2+2+3)',
};
